import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import BuyCrypto from '../component/BuyCrypto.js';
import PostMessageBridge from '../utils/postMessageBridge.js';
import { parseWidgetConfig } from '../config/widgetConfig.js';

/**
 * WidgetPage - Standalone widget page for iframe embedding
 *
 * URL Parameters:
 * - embed=true       : Enable embedded mode (removes outer styling)
 * - fiat=USD         : Default fiat currency
 * - crypto=ETH       : Default cryptocurrency
 * - network=ethereum : Default blockchain network
 * - amount=100       : Default amount
 * - wallet=0x...     : Pre-filled wallet address
 * - primaryColor=%237c3aed : Primary theme color (URL encoded)
 * - bgColor=%23ffffff : Background color (URL encoded)
 * - borderRadius=16  : Border radius in pixels
 * - hideHeader=true  : Hide the widget header
 * - hideFooter=true  : Hide the widget footer
 * - providers=transak,banxa : Comma-separated list of allowed providers
 * - origin=https://yoursite.com : Allowed parent origin for postMessage
 * - paymentMethod=card : Pre-select payment method
 * - provider=transak : Pre-select provider
 * - lockWallet=true  : Lock wallet address field (readonly)
 * - lockAmount=true  : Lock amount field (readonly)
 * - lockFiat=true    : Lock fiat currency selector
 * - lockCrypto=true  : Lock crypto/network selector
 * - merchantId=xxx   : Merchant identification
 * - orderId=xxx      : Order identification
 * - successUrl=...   : Callback URL on success
 * - failureUrl=...   : Callback URL on failure
 *
 * Example URLs:
 * - http://localhost:3000/widget?embed=true
 * - http://localhost:3000/widget?embed=true&fiat=EUR&crypto=BTC
 * - http://localhost:3000/widget?embed=true&wallet=0x...&lockWallet=true
 * - http://localhost:3000/widget?embed=true&primaryColor=%23ff6600
 */
const WidgetPage = () => {
  const [searchParams] = useSearchParams();

  // Parse configuration from URL parameters (single source of truth)
  const config = useMemo(() => parseWidgetConfig(), [searchParams]);

  // Initialize postMessage bridge
  const bridge = useMemo(() => {
    const b = new PostMessageBridge(config.parentOrigin);
    b.init();
    return b;
  }, [config.parentOrigin]);

  // Apply theme configuration
  useEffect(() => {
    const root = document.documentElement;

    if (config.primaryColor) {
      root.style.setProperty('--primary-color', config.primaryColor);
      // Generate a darker hover color
      const hoverColor = adjustColorBrightness(config.primaryColor, -20);
      root.style.setProperty('--primary-hover', hoverColor);
    }

    if (config.backgroundColor) {
      root.style.setProperty('--widget-bg', config.backgroundColor);
    }

    if (config.borderRadius) {
      root.style.setProperty('--border-radius', `${config.borderRadius}px`);
    }

    // Set body styles for embedded mode
    if (config.isEmbedded) {
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      document.body.style.overflow = 'hidden';
      document.body.style.background = 'transparent';
    }

    // Apply dark theme to body
    if (config.theme === 'dark') {
      document.body.style.background = '#0f0f1a';
      document.body.classList.add('dark-theme');
    }

    return () => {
      // Cleanup on unmount
      root.style.removeProperty('--primary-color');
      root.style.removeProperty('--primary-hover');
      root.style.removeProperty('--widget-bg');
      root.style.removeProperty('--border-radius');
      document.body.classList.remove('dark-theme');
    };
  }, [config]);

  return (
    <BuyCrypto
      config={config}
      bridge={bridge}
      isEmbedded={config.isEmbedded}
    />
  );
};

/**
 * Adjust color brightness
 * @param {string} hex - Hex color string
 * @param {number} amount - Amount to adjust (-255 to 255)
 * @returns {string} Adjusted hex color
 */
function adjustColorBrightness(hex, amount) {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Handle short hex format
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }

  // Parse RGB values
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Adjust brightness
  r = Math.max(0, Math.min(255, r + amount));
  g = Math.max(0, Math.min(255, g + amount));
  b = Math.max(0, Math.min(255, b + amount));

  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export default WidgetPage;
